import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Brain,
  TrendingUp,
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  Info,
  ChevronRight,
  BarChart3,
  Target,
  Zap,
  Database,
  Settings,
  Play,
  Lock,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const LeadScoringPage = () => {
  const { sessionToken, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analysisStatus, setAnalysisStatus] = useState({
    lastRun: null,
    contactsAnalyzed: 0,
    modelsReady: false,
    scopesConfigured: {
      contacts: true,
      deals: true,
      meetings: true,
      notes: true,
      emails: false, // Needs sales-email-read scope
      invoices: false, // Needs commerce scopes
    }
  });
  const [leadScores, setLeadScores] = useState([]);
  const [featureImportance, setFeatureImportance] = useState([]);
  const [insights, setInsights] = useState([]);

  // Placeholder data for UI demonstration
  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setAnalysisStatus({
        lastRun: null,
        contactsAnalyzed: 0,
        modelsReady: false,
        scopesConfigured: {
          contacts: true,
          deals: true,
          meetings: true,
          notes: true,
          emails: false,
          invoices: false,
        }
      });
      
      // Placeholder insights from our analysis
      setInsights([
        {
          type: "pattern",
          title: "Meetings Drive Conversions",
          description: "Companies with 4+ meetings convert 3.2x more often than those with fewer.",
          importance: 0.44,
          icon: Users,
        },
        {
          type: "warning",
          title: "CRM Data Gaps Detected",
          description: "Multiple paying customers have no deal records in HubSpot, impacting forecast accuracy.",
          importance: 0.35,
          icon: AlertTriangle,
        },
        {
          type: "pattern",
          title: "Engagement Score Correlation",
          description: "Contacts with engagement scores above 7 have 67% lower churn risk.",
          importance: 0.28,
          icon: TrendingUp,
        },
        {
          type: "churn",
          title: "Zero Meeting = High Churn",
          description: "100% of churned customers (PRIMO, LoadBoard) had zero logged meetings before disengagement.",
          importance: 0.22,
          icon: Clock,
        },
      ]);
      
      // Placeholder feature importance from Random Forest
      setFeatureImportance([
        { feature: "meetings_count", importance: 0.44, label: "Meeting Count" },
        { feature: "notes_count", importance: 0.22, label: "Notes Count" },
        { feature: "engagement_score", importance: 0.18, label: "Engagement Score" },
        { feature: "total_contacts", importance: 0.10, label: "Total Contacts" },
        { feature: "calls_count", importance: 0.06, label: "Calls Count" },
      ]);
      
      setLoading(false);
    }, 1000);
  }, []);

  const handleRunAnalysis = () => {
    toast.info("Analysis requires email and invoice scopes to be configured in HubSpot first.");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-[#00A5A8] mx-auto mb-4" />
          <p className="text-slate-600">Loading Lead Scoring...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Brain className="w-8 h-8 text-[#00A5A8]" />
            Predictive Lead Scoring
          </h1>
          <p className="text-slate-500 mt-1">
            AI-powered insights from HubSpot activity patterns and payment history
          </p>
        </div>
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
          <Lock className="w-3 h-3 mr-1" />
          JCI Internal Only
        </Badge>
      </div>

      {/* Status Banner */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Settings className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800">Configuration Required</h3>
              <p className="text-sm text-amber-700 mt-1">
                To enable full predictive analysis, the following HubSpot OAuth scopes need to be added:
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Badge variant={analysisStatus.scopesConfigured.emails ? "default" : "outline"} 
                       className={analysisStatus.scopesConfigured.emails ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}>
                  {analysisStatus.scopesConfigured.emails ? <CheckCircle className="w-3 h-3 mr-1" /> : <Lock className="w-3 h-3 mr-1" />}
                  sales-email-read
                </Badge>
                <Badge variant={analysisStatus.scopesConfigured.invoices ? "default" : "outline"}
                       className={analysisStatus.scopesConfigured.invoices ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}>
                  {analysisStatus.scopesConfigured.invoices ? <CheckCircle className="w-3 h-3 mr-1" /> : <Lock className="w-3 h-3 mr-1" />}
                  crm.objects.invoices.read
                </Badge>
                <Badge variant={analysisStatus.scopesConfigured.invoices ? "default" : "outline"}
                       className={analysisStatus.scopesConfigured.invoices ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}>
                  {analysisStatus.scopesConfigured.invoices ? <CheckCircle className="w-3 h-3 mr-1" /> : <Lock className="w-3 h-3 mr-1" />}
                  crm.objects.subscriptions.read
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Info className="w-5 h-5 text-[#00A5A8]" />
            How Predictive Lead Scoring Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-[#00A5A8] text-white flex items-center justify-center text-xs font-bold">1</div>
                <span className="font-semibold text-slate-700">Data Collection</span>
              </div>
              <p className="text-sm text-slate-600">
                Pull HubSpot activities (meetings, notes, calls, emails) + payment/invoice data as ground truth
              </p>
            </div>
            
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-[#00A5A8] text-white flex items-center justify-center text-xs font-bold">2</div>
                <span className="font-semibold text-slate-700">LLM Analysis</span>
              </div>
              <p className="text-sm text-slate-600">
                GPT-4.1-nano reconciles CRM vs actual conversions, infers missing stage dates, extracts signals
              </p>
            </div>
            
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-[#00A5A8] text-white flex items-center justify-center text-xs font-bold">3</div>
                <span className="font-semibold text-slate-700">ML Training</span>
              </div>
              <p className="text-sm text-slate-600">
                sklearn Random Forest learns which activities predict conversion/churn from cleaned data
              </p>
            </div>
            
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-[#00A5A8] text-white flex items-center justify-center text-xs font-bold">4</div>
                <span className="font-semibold text-slate-700">Live Scoring</span>
              </div>
              <p className="text-sm text-slate-600">
                Score new leads instantly (milliseconds, 100% private on your server) with conversion probability
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preliminary Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pattern Insights */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="w-5 h-5 text-amber-500" />
              Preliminary Insights
            </CardTitle>
            <CardDescription>
              Based on initial analysis of 6 customer records
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {insights.map((insight, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className={`p-2 rounded-lg ${
                  insight.type === "warning" ? "bg-amber-100" : 
                  insight.type === "churn" ? "bg-red-100" : "bg-green-100"
                }`}>
                  <insight.icon className={`w-4 h-4 ${
                    insight.type === "warning" ? "text-amber-600" : 
                    insight.type === "churn" ? "text-red-600" : "text-green-600"
                  }`} />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-slate-800">{insight.title}</h4>
                  <p className="text-sm text-slate-600 mt-1">{insight.description}</p>
                </div>
                <Badge variant="outline" className="text-xs">
                  {Math.round(insight.importance * 100)}% signal
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Feature Importance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="w-5 h-5 text-[#00A5A8]" />
              Feature Importance
            </CardTitle>
            <CardDescription>
              What predicts conversion/churn (from Random Forest model)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {featureImportance.map((item, i) => (
              <div key={i} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700">{item.label}</span>
                  <span className="text-slate-500">{Math.round(item.importance * 100)}%</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-[#00A5A8] to-[#007A7C] rounded-full transition-all duration-500"
                    style={{ width: `${item.importance * 100}%` }}
                  />
                </div>
              </div>
            ))}
            
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> These results are from a sample of 6 records. 
                With 100+ records after adding scopes, accuracy will significantly improve.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost & Security Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Database className="w-5 h-5 text-green-600" />
              Cost Estimate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-600">LLM Analysis (one-time)</span>
                <span className="font-semibold text-slate-800">~$1.50 - $3.00</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-600">ML Training & Scoring</span>
                <span className="font-semibold text-green-600">Free (runs locally)</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-600">Ongoing Lead Scoring</span>
                <span className="font-semibold text-green-600">Free (no API calls)</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lock className="w-5 h-5 text-green-600" />
              Data Security
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-800">OpenAI API (LLM)</p>
                  <p className="text-sm text-slate-600">Data encrypted, not used for training, deleted in 30 days</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-800">sklearn (ML Model)</p>
                  <p className="text-sm text-slate-600">100% local, never leaves your server, no internet required</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-800">Trained Model</p>
                  <p className="text-sm text-slate-600">Stored as .pkl file on your infrastructure only</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Button */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-800">Ready to Run Full Analysis?</h3>
              <p className="text-sm text-slate-600 mt-1">
                After adding email and invoice scopes, click to analyze all contacts and build your predictive model.
              </p>
            </div>
            <Button 
              onClick={handleRunAnalysis}
              disabled={!analysisStatus.scopesConfigured.emails || !analysisStatus.scopesConfigured.invoices}
              className="bg-[#00A5A8] hover:bg-[#008B8E]"
            >
              <Play className="w-4 h-4 mr-2" />
              Run Analysis
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Footer Note */}
      <div className="text-center text-sm text-slate-500 pb-6">
        <p>
          This feature uses GPT-4.1-nano for data cleaning (~$0.0003/contact) and sklearn for predictions (free).
          <br />
          All ML training and scoring happens locally on your server.
        </p>
      </div>
    </div>
  );
};

export default LeadScoringPage;
